import React, {useState, useEffect, useCallback} from "react";
import "../../components/propertydetails/LiveTourFormModal.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateRoomName(propertySlug) {
    const rand = Math.random().toString(36).slice(2, 8);
    return `ogm-${propertySlug || "tour"}-${rand}`;
}

// ✅ Replace the existing generateSlots() in BOTH files:
function generateSlots() {
    const slots = [];
    const now = new Date();

    for (let d = 0; d < 14; d++) {          // 14 days instead of 7
        const date = new Date(now);
        date.setDate(now.getDate() + d + 1);
        const dateStr = date.toLocaleDateString("en-IN", {
            weekday: "short", month: "short", day: "numeric",
        });

        // Hourly slots 9AM → 8PM (12 options per day)
        for (let h = 9; h <= 20; h++) {
            // Skip past slots for today+1 (compare with current time)
            const slotTime = new Date(date);
            slotTime.setHours(h, 0, 0, 0);
            if (d === 0 && slotTime <= now) continue; // skip past times

            const iso = slotTime.toISOString().slice(0, 16);
            const hr = h > 12 ? h - 12 : h === 12 ? 12 : h;
            const ap = h >= 12 ? "PM" : "AM";
            const mins = "00";
            slots.push({
                dateStr,
                date: date.toDateString(),
                label: `${hr}:${mins} ${ap}`,
                iso,
            });
        }
    }
    return slots;
}

// ── STEP 1 — Customer Details Form ────────────────────────────────────────────

function StepDetails({form, onChange, onSubmit, loading, error, property}) {
    return (
        <div className="ltf-step">
            <div className="ltf-hero">
                <span className="ltf-live-dot" aria-hidden="true"/>
                <span className="ltf-hero-text">Join Live Video Tour</span>
            </div>
            <p className="ltf-property-name">{property?.title}</p>
            <p className="ltf-sub">
                Connect instantly with our property advisor via live video.
                We'll call you back within 2 minutes.
            </p>

            {error && (
                <div className="ltf-error" role="alert">{error}</div>
            )}

            <div className="ltf-fields">
                <div className="ltf-field">
                    <label htmlFor="ltf-name" className="ltf-label">Full Name *</label>
                    <input
                        id="ltf-name"
                        type="text"
                        className="ltf-input"
                        placeholder="Rahul Sharma"
                        value={form.fullName}
                        onChange={e => onChange("fullName", e.target.value)}
                        autoComplete="name"
                        required
                    />
                </div>

                <div className="ltf-field">
                    <label htmlFor="ltf-email" className="ltf-label">Email</label>
                    <input
                        id="ltf-email"
                        type="email"
                        className="ltf-input"
                        placeholder="rahul@email.com (optional)"
                        value={form.email}
                        onChange={e => onChange("email", e.target.value)}
                        autoComplete="email"
                    />
                </div>

                <div className="ltf-field">
                    <label htmlFor="ltf-wa" className="ltf-label">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
                            <path
                                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                            <path
                                d="M12 0C5.373 0 0 5.373 0 12c0 2.136.557 4.143 1.531 5.886L.05 23.95l6.264-1.464A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.937 0-3.745-.525-5.294-1.441l-.38-.226-3.718.869.934-3.621-.248-.393A9.795 9.795 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
                        </svg>
                        WhatsApp Number *
                    </label>
                    <div className="ltf-phone-wrap">
                        <span className="ltf-prefix">+91</span>
                        <input
                            id="ltf-wa"
                            type="tel"
                            className="ltf-input ltf-phone-input"
                            placeholder="9876543210"
                            value={form.whatsapp}
                            onChange={e => onChange("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))}
                            autoComplete="tel"
                            required
                            inputMode="numeric"
                        />
                    </div>
                    <span className="ltf-hint">We'll send updates on WhatsApp</span>
                </div>
            </div>

            <button
                className="ltf-submit-btn"
                onClick={onSubmit}
                disabled={loading}
            >
                {loading ? (
                    <><span className="ltf-spinner"/> Connecting…</>
                ) : (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polygon points="23 7 16 12 23 17 23 7"/>
                            <rect x="1" y="5" width="15" height="14" rx="2"/>
                        </svg>
                        Start Live Tour
                    </>
                )}
            </button>

            <p className="ltf-footer-note">
                Agent will join within 2 minutes. If unavailable, you can book a slot.
            </p>
        </div>
    );
}

// ── STEP 2 — Waiting for agent ────────────────────────────────────────────────

function StepWaiting({requestId, onAgentJoined, onNoResponse, onScheduleInstead, elapsed}) {
    const dots = ".".repeat((Math.floor(elapsed / 1) % 3) + 1);

    return (
        <div className="ltf-step ltf-waiting">
            <div className="ltf-pulse-ring" aria-hidden="true"/>
            <div className="ltf-pulse-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7"/>
                    <rect x="1" y="5" width="15" height="14" rx="2"/>
                </svg>
            </div>

            <h3 className="ltf-waiting-title">Connecting to Agent{dots}</h3>
            <p className="ltf-waiting-sub">
                Our property advisor is being notified. Please keep this window open.
            </p>

            <div className="ltf-timer-bar-wrap">
                <div
                    className="ltf-timer-bar"
                    style={{width: `${Math.max(0, 100 - (elapsed / 120) * 100)}%`}}
                />
            </div>
            <p className="ltf-timer-text">
                {elapsed < 120
                    ? `Waiting ${120 - elapsed}s…`
                    : "Agent hasn't responded yet"}
            </p>

            {elapsed >= 60 && (
                <div className="ltf-no-response-box">
                    <p className="ltf-no-response-msg">
                        ⏰ Agent seems busy right now. You can wait or schedule a convenient slot.
                    </p>
                    <button className="ltf-schedule-btn" onClick={onScheduleInstead}>
                        📅 Book a Slot Instead
                    </button>
                </div>
            )}
        </div>
    );
}

// ── STEP 3 — Schedule a slot ──────────────────────────────────────────────────

function StepSchedule({requestId, property, form, onScheduled, onBack}) {
    const slots = generateSlots();
    const [selDate, setSelDate] = useState(slots[0]?.date || "");
    const [selSlot, setSelSlot] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const dates = [...new Set(slots.map(s => s.date))];
    const daySlots = slots.filter(s => s.date === selDate);
    const activeDateLabel = slots.find(s => s.date === selDate)?.dateStr || "";

    const handleBook = async () => {
        if (!selSlot) {
            setError("Please select a time slot.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const res = await fetch(`${API_BASE}/api/live-tour/schedule`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    requestId,
                    propertyId: property?.id,
                    propertyTitle: property?.title,
                    customerName: form.fullName,
                    customerEmail: form.email,
                    whatsappNumber: form.whatsapp,
                    scheduledAt: selSlot.iso,
                    note: `Scheduled live tour for ${property?.title}`,
                }),
            });
            if (!res.ok) throw new Error();
            onScheduled(selSlot);
        } catch {
            setError("Booking failed. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ltf-step ltf-schedule">
            <h3 className="ltf-section-title">📅 Book a Live Tour Slot</h3>
            <p className="ltf-sub">Pick a date and time — our agent will call you via video.</p>

            {/* Date pills */}
            <div className="ltf-date-row">
                {dates.map(d => {
                    const label = slots.find(s => s.date === d)?.dateStr || d;
                    return (
                        <button
                            key={d}
                            className={`ltf-date-pill${selDate === d ? " active" : ""}`}
                            onClick={() => {
                                setSelDate(d);
                                setSelSlot(null);
                            }}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>

            {/* Time slots */}
            <p className="ltf-slots-label">{activeDateLabel}</p>
            <div className="ltf-slots-grid">
                {daySlots.map(t => (
                    <button
                        key={t.iso}
                        className={`ltf-slot${selSlot?.iso === t.iso ? " active" : ""}`}
                        onClick={() => setSelSlot(t)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {error && <div className="ltf-error">{error}</div>}

            <button
                className="ltf-submit-btn"
                onClick={handleBook}
                disabled={saving || !selSlot}
            >
                {saving ? <><span className="ltf-spinner"/> Booking…</> : "Confirm Slot →"}
            </button>

            <button className="ltf-back-link" onClick={onBack}>← Back</button>
        </div>
    );
}

// ── STEP 4 — Confirmed ────────────────────────────────────────────────────────

function StepConfirmed({slot, form, property, onClose}) {
    return (
        <div className="ltf-step ltf-confirmed">
            <div className="ltf-check-circle" aria-hidden="true">✓</div>
            <h3 className="ltf-confirmed-title">Tour Scheduled!</h3>
            <p className="ltf-confirmed-sub">
                We'll reach you on WhatsApp +91{form.whatsapp} and
                {form.email ? ` email ${form.email}` : ""} before the call.
            </p>

            <div className="ltf-confirmed-card">
                <div className="ltf-cc-row">
                    <span>🏠 Property</span>
                    <strong>{property?.title}</strong>
                </div>
                <div className="ltf-cc-row">
                    <span>📅 Date &amp; Time</span>
                    <strong>{slot?.label} · {slot?.dateStr || new Date(slot?.iso).toLocaleDateString("en-IN", {
                        weekday: "short",
                        month: "short",
                        day: "numeric"
                    })}</strong>
                </div>
                <div className="ltf-cc-row">
                    <span>👤 Name</span>
                    <strong>{form.fullName}</strong>
                </div>
                <div className="ltf-cc-row">
                    <span>📱 WhatsApp</span>
                    <strong>+91 {form.whatsapp}</strong>
                </div>
            </div>

            <p className="ltf-confirmed-note">
                A confirmation has been sent to your WhatsApp{form.email ? " and email" : ""}.
                Our agent will call you at the scheduled time.
            </p>

            <button className="ltf-submit-btn ltf-submit-btn--green" onClick={onClose}>
                Done
            </button>
        </div>
    );
}

// ── WebSocket helper ─────────────────────────────────────────────────────────
// Called BEFORE the API request so the subscription is live as early as possible.
// Uses a ref for the client so it survives across renders.
// Uses a ref for the handler so the waiting-step useEffect can wire it in later.
function setupWsSubscription(room, wsClientRef, onJoinedRef) {
    // Tear down any previous connection first
    try { wsClientRef.current?.disconnect?.(); } catch { /* ignore */ }
    wsClientRef.current = null;

    const subscribe = () => {
        if (!room || !window.SockJS || !window.Stomp) return;
        try {
            const socket = new window.SockJS(`${process.env.REACT_APP_API_BASE || ""}/live-queue`);
            const client = window.Stomp.over(socket);
            client.debug  = null;
            client.connect(
                {},
                () => {
                    wsClientRef.current = client;
                    client.subscribe(
                        `/topic/customer/${room}/call-accepted`,
                        (msg) => {
                            try {
                                const d = JSON.parse(msg.body);
                                if (d.accepted === true || d.type === "AGENT_JOINED") {
                                    // Fire the handler that the waiting useEffect wired in
                                    onJoinedRef.current?.(d.roomName || room);
                                }
                            } catch { /* malformed message */ }
                        }
                    );
                },
                () => { wsClientRef.current = null; /* WS error — HTTP poll covers it */ }
            );
        } catch { /* WS unavailable — HTTP poll covers it */ }
    };

    if (window.SockJS && window.Stomp) {
        subscribe();
    } else {
        const s1 = document.createElement("script");
        s1.src    = "https://cdnjs.cloudflare.com/ajax/libs/sockjs-client/1.6.1/sockjs.min.js";
        s1.onload = () => {
            const s2 = document.createElement("script");
            s2.src    = "https://cdnjs.cloudflare.com/ajax/libs/stomp.js/2.3.3/stomp.min.js";
            s2.onload = subscribe;
            document.body.appendChild(s2);
        };
        document.body.appendChild(s1);
    }
}

// ── MAIN MODAL ────────────────────────────────────────────────────────────────

export default function LiveTourFormModal({property, onClose, onJoinCall}) {
    const [step, setStep] = useState("details"); // details | schedule | confirmed
    const [form, setForm] = useState({fullName: "", email: "", whatsapp: ""});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [slot, setSlot] = useState(null);

    const handleChange = useCallback((field, value) => {
        setForm(prev => ({...prev, [field]: value}));
        setError("");
    }, []);

    const handleSubmit = async () => {
        if (!form.fullName.trim()) {
            setError("Please enter your name.");
            return;
        }
        if (!form.whatsapp.trim() || form.whatsapp.length < 10) {
            setError("Please enter a valid 10-digit WhatsApp number.");
            return;
        }
        setLoading(true);
        setError("");

        const room = generateRoomName(property?.slug);

        try {
            const res = await fetch(`${API_BASE}/api/live-tour/request`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    propertyId:    property?.id,
                    propertyTitle: property?.title,
                    propertySlug:  property?.slug,
                    fullName:      form.fullName.trim(),
                    email:         form.email.trim() || null,
                    whatsappNumber:form.whatsapp.trim(),
                    roomName:      room,
                }),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();

            // Join Jitsi immediately — no waiting screen.
            // Use the roomName from the backend (handles dedup — same customer
            // resubmitting gets the same room the agent is already in).
            const activeRoom = data.roomName || room;
            onJoinCall?.({
                name:     form.fullName.trim(),
                initials: form.fullName.trim().split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
                property: property?.title,
                mobile:   form.whatsapp.trim(),
                email:    form.email.trim() || null,
                roomName: activeRoom,
            });
            onClose();
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="ltf-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Live video tour"
            onClick={e => e.target === e.currentTarget && onClose()}
        >
            <div className="ltf-modal">
                <button className="ltf-close" onClick={onClose} aria-label="Close">✕</button>

                {step === "details" && (
                    <StepDetails
                        form={form}
                        onChange={handleChange}
                        onSubmit={handleSubmit}
                        loading={loading}
                        error={error}
                        property={property}
                    />
                )}

                {step === "schedule" && (
                    <StepSchedule
                        requestId={null}
                        property={property}
                        form={form}
                        onScheduled={(s) => { setSlot(s); setStep("confirmed"); }}
                        onBack={() => setStep("details")}
                    />
                )}

                {step === "confirmed" && (
                    <StepConfirmed
                        slot={slot}
                        form={form}
                        property={property}
                        onClose={onClose}
                    />
                )}
            </div>
        </div>
    );
}